// BlockItem — wrapper for a single block in the BlockEditor list.
//
// Responsibilities:
//   • Sortable behavior via @dnd-kit useSortable (drag-handle on the left).
//   • Hover-visible gutter UI: handle + per-block menu.
//   • Floating BlockToolbar above the block when selected.
//   • Validation warning below the block (red) when validateBlock fails.
//   • Routes block-type-specific editing to the appropriate child editor
//     under ./blocks/*.
//
// We deliberately keep the block dispatching local: there's no registry —
// just a switch statement. It's fewer abstractions for 14 fixed types.

import { CSSProperties } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Block, BlockType } from "../../lib/blocks";
import { BlockToolbar } from "./BlockToolbar";
import { t } from "./theme";

import { HeadingBlockEditor } from "./blocks/HeadingBlockEditor";
import { ParagraphBlockEditor } from "./blocks/ParagraphBlockEditor";
import { QuoteBlockEditor } from "./blocks/QuoteBlockEditor";
import { ImageBlockEditor } from "./blocks/ImageBlockEditor";
import { GalleryBlockEditor } from "./blocks/GalleryBlockEditor";
import { VideoEmbedBlockEditor } from "./blocks/VideoEmbedBlockEditor";
import { VideoFileBlockEditor } from "./blocks/VideoFileBlockEditor";
import { AudioEmbedBlockEditor } from "./blocks/AudioEmbedBlockEditor";
import { AudioFileBlockEditor } from "./blocks/AudioFileBlockEditor";
import { LinkCardBlockEditor } from "./blocks/LinkCardBlockEditor";
import { CaptionBlockEditor } from "./blocks/CaptionBlockEditor";
import { DividerBlockEditor } from "./blocks/DividerBlockEditor";
import { ListBlockEditor } from "./blocks/ListBlockEditor";
import { SpacerBlockEditor } from "./blocks/SpacerBlockEditor";
import { CtaBlockEditor } from "./blocks/CtaBlockEditor";
import { HeroBlockEditor } from "./blocks/HeroBlockEditor";
import { EmbedBlockEditor } from "./blocks/EmbedBlockEditor";

export interface BlockItemProps {
  block: Block;
  index: number;
  total: number;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<Block>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMove: (dir: -1 | 1) => void;
  onSlashMenu: () => void;
  onInsertAfter: (type: BlockType) => void;
  replaceSelf: (type: BlockType) => void;
  /** Magic-paste callback: пользователь вставил Markdown/HTML, который
   *  распарсился в массив блоков. */
  onPasteBlocks?: (blocks: Block[]) => void;
  validationMsg: string | null;
  articleId: string;
  mediaAssets: string[];
  onUploadFile: (file: File) => Promise<string>;
}

export function BlockItem(props: BlockItemProps) {
  const {
    block,
    isSelected,
    onSelect,
    onChange,
    onRemove,
    onDuplicate,
    onMove,
    onSlashMenu,
    onInsertAfter,
    replaceSelf,
    onPasteBlocks,
    validationMsg,
    articleId,
    mediaAssets,
    onUploadFile,
    index,
    total,
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const wrapperStyle: CSSProperties = {
    position: "relative",
    padding: "2px 0",
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const innerStyle: CSSProperties = {
    position: "relative",
    padding: "8px 12px",
    borderRadius: 6,
    border: `1px solid ${isSelected ? t.selectedBorder : "transparent"}`,
    background: isSelected ? t.selectedBg : "transparent",
    transition: t.transition,
  };

  return (
    <div ref={setNodeRef} style={wrapperStyle} onClick={onSelect}>
      {/* Drag handle, in the left gutter. */}
      <button
        type="button"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
        style={{
          position: "absolute",
          left: -36,
          top: 12,
          width: 24,
          height: 24,
          padding: 0,
          background: "transparent",
          border: "none",
          color: t.text4,
          fontSize: 14,
          lineHeight: 1,
          cursor: "grab",
          opacity: isSelected ? 1 : 0,
          transition: "opacity 200ms ease",
          touchAction: "none",
        }}
        onMouseEnter={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.opacity = "1")
        }
        onFocus={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.opacity = "1")
        }
      >
        ⋮⋮
      </button>

      <div
        style={innerStyle}
        onMouseEnter={(e) => {
          const handle = (
            e.currentTarget.parentElement?.querySelector(
              'button[aria-label="Drag to reorder"]',
            ) as HTMLElement | null
          );
          if (handle) handle.style.opacity = "1";
        }}
        onMouseLeave={(e) => {
          if (isSelected) return;
          const handle = (
            e.currentTarget.parentElement?.querySelector(
              'button[aria-label="Drag to reorder"]',
            ) as HTMLElement | null
          );
          if (handle) handle.style.opacity = "0";
        }}
      >
        {isSelected && (
          <BlockToolbar
            block={block}
            canMoveUp={index > 0}
            canMoveDown={index < total - 1}
            onMove={onMove}
            onDuplicate={onDuplicate}
            onRemove={onRemove}
            replaceSelf={replaceSelf}
          />
        )}
        {renderBlockEditor(block, {
          isSelected,
          onChange,
          onSlashMenu,
          onInsertAfter,
          onPasteBlocks,
          articleId,
          mediaAssets,
          onUploadFile,
        })}
      </div>

      {validationMsg && isSelected && (
        <div
          style={{
            margin: "4px 12px 0",
            padding: "4px 8px",
            fontSize: 11,
            color: t.danger,
            background: t.dangerSoft,
            border: `1px solid ${t.danger}40`,
            borderRadius: 4,
            fontFamily: t.sans,
          }}
        >
          {validationMsg}
        </div>
      )}
    </div>
  );
}

// Dispatch table — one switch over 14 types.
function renderBlockEditor(
  block: Block,
  ctx: {
    isSelected: boolean;
    onChange: (patch: Partial<Block>) => void;
    onSlashMenu: () => void;
    onInsertAfter: (type: BlockType) => void;
    onPasteBlocks?: (blocks: Block[]) => void;
    articleId: string;
    mediaAssets: string[];
    onUploadFile: (file: File) => Promise<string>;
  },
) {
  switch (block.type) {
    case "heading":
      return <HeadingBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "paragraph":
      return (
        <ParagraphBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
          onSlashMenu={ctx.onSlashMenu}
          onInsertAfter={() => ctx.onInsertAfter("paragraph")}
          onPasteBlocks={ctx.onPasteBlocks}
        />
      );
    case "quote":
      return <QuoteBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "image":
      return (
        <ImageBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
          articleId={ctx.articleId}
          mediaAssets={ctx.mediaAssets}
          onUploadFile={ctx.onUploadFile}
        />
      );
    case "gallery":
      return (
        <GalleryBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
          articleId={ctx.articleId}
          mediaAssets={ctx.mediaAssets}
          onUploadFile={ctx.onUploadFile}
        />
      );
    case "video_embed":
      return (
        <VideoEmbedBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
        />
      );
    case "video_file":
      return (
        <VideoFileBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
          articleId={ctx.articleId}
          mediaAssets={ctx.mediaAssets}
          onUploadFile={ctx.onUploadFile}
        />
      );
    case "audio_embed":
      return (
        <AudioEmbedBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
        />
      );
    case "audio_file":
      return (
        <AudioFileBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
          articleId={ctx.articleId}
          mediaAssets={ctx.mediaAssets}
          onUploadFile={ctx.onUploadFile}
        />
      );
    case "link_card":
      return (
        <LinkCardBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
        />
      );
    case "caption":
      return <CaptionBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "divider":
      return <DividerBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "list":
      return <ListBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "spacer":
      return <SpacerBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "cta":
      return <CtaBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
    case "hero":
      return (
        <HeroBlockEditor
          block={block}
          onChange={ctx.onChange}
          isSelected={ctx.isSelected}
          articleId={ctx.articleId}
          mediaAssets={ctx.mediaAssets}
          onUploadFile={ctx.onUploadFile}
        />
      );
    case "embed":
      return <EmbedBlockEditor block={block} onChange={ctx.onChange} isSelected={ctx.isSelected} />;
  }
}
